import { create } from "zustand";

import { Theme, CSSVariables } from "./theme/theme.types";
import { FluxoType, Partner, PartnerConfig, Publico } from "./tenant.types";
import { Assets } from "./assets/assets.types";
import { Texts } from "./texts/texts.types";

import { themes } from "./theme/themes";
import { assets } from "./assets/assets";
import { sellerTexts, texts } from "./texts/texts";
import { resolverMarca } from "./brand/resolver-marca";
import { resolverTenantUrl } from "./resolver-tenant-url";

type Features = {
  isCPFCustomersAllowed: boolean;
  isChoosePlanAllowed: boolean;
  shouldLoadAnalytics: boolean;
  shouldLoadChat: boolean;
  isApplyCouponAllowed: boolean;
  shouldShowHomeCalculator: boolean;
};

type TenantState = {
  theme: Theme;
  assets: Assets;
  partner: Partner | null;
  partnerId: string | null;
  brand: PartnerConfig;
  type: FluxoType | null;
  texts: Texts;
  setPartner: (partner: Partner) => void;
  features: Features;
};

const updateCSSVariables = (variables: CSSVariables) => {
  const root = document.documentElement;
  Object.entries(variables).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
};

const updateFavicon = (favicon: string) => {
  const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
  link.href = favicon;
};

const getTextsForType = (type: FluxoType, brand: PartnerConfig): Texts => {
  return type === FluxoType.CONSULTOR ? sellerTexts[`${brand}_SELLER`] : texts[brand];
};

const isCPFCustomersAllowed = (publico: Publico) => {
  return publico === Publico.PESSOA_FISICA || publico === Publico.MISTO;
};

const RECURSOS_POR_MARCA: Record<PartnerConfig, { analytics: boolean; chat: boolean; calculadoraNaHome: boolean }> = {
  DEFAULT: { analytics: false, chat: false, calculadoraNaHome: false },
  BENUP: { analytics: true, chat: false, calculadoraNaHome: false },
};

const carregaScripts = (brand: PartnerConfig, partner: Partner) => {
  if (brand !== "DEFAULT") return RECURSOS_POR_MARCA[brand];

  const ehIvi = partner.nome?.toLowerCase()?.includes("ivi") ?? false;
  return { analytics: ehIvi, chat: ehIvi };
};

export const useTenantStore = create<TenantState>((set, get) => ({
  theme: themes.DEFAULT,
  assets: assets.DEFAULT,
  partner: null,
  partnerId: null,
  brand: "DEFAULT",
  type: null,
  texts: texts.DEFAULT,
  features: {
    isCPFCustomersAllowed: true,
    isChoosePlanAllowed: false,
    shouldLoadAnalytics: false,
    shouldLoadChat: false,
    isApplyCouponAllowed: false,
    shouldShowHomeCalculator: false,
  },

  setPartner: (partner: Partner) => {
    const brand = resolverMarca(resolverTenantUrl());
    const theme = themes[brand];
    const brandAssets = assets[brand];

    updateCSSVariables(theme.cssVariables);
    updateFavicon(brandAssets.favicon);

    const scripts = carregaScripts(brand, partner);

    set({
      theme,
      assets: brandAssets,
      brand,
      partner: partner,
      partnerId: partner.id,
      type: partner.fluxo.tipo,
      texts: getTextsForType(partner.fluxo.tipo, brand),
      features: {
        isCPFCustomersAllowed: isCPFCustomersAllowed(partner.fluxo.publico),
        isChoosePlanAllowed: partner.fluxo.podeEscolherPlano,
        shouldLoadAnalytics: scripts.analytics,
        shouldLoadChat: scripts.chat,
        isApplyCouponAllowed: partner.fluxo.podeAplicarCupom,
        shouldShowHomeCalculator: RECURSOS_POR_MARCA[brand].calculadoraNaHome,
      },
    });

    console.log(`Parceiro definido: ${partner.nome}`);
    console.log(`Tipo de fluxo definido: ${partner.fluxo.tipo}`);
    console.log(`Marca definida: ${brand}`);
  },
}));
